import { z } from "zod";

import { FilledDynamicValueTableColumnTypeLiteral } from "../DynamicValue.js";
import { ExploreFieldId, ExploreFieldTypeLiteral } from "../explore/types.js";
// from the uniquely named module, not `../hql/types.js`: the Python kernel
// type generator flattens by basename and `hql/types.ts` loses to
// `chart/types.ts`, which would resolve this to `any` and collapse the whole
// inferred type of `ColumnFilter`
import { HqlAggregationFunctionLiteral } from "../hql/hqlAggregationFunctions.js";
import {
  AppSessionId,
  CellId,
  DataSourceTableId,
  DisplayTableColumnId,
  SemanticDatasetName,
  SemanticProjectId,
} from "../idTypeBrands.js";
import { SemanticProjectVersionString } from "../semantic-layer/semanticProjectVersionTypes.js";

import { ColumnPredicate } from "./columnPredicateTypes.js";

export const ColumnFilter = z.object({
  column: DisplayTableColumnId,
  columnType: FilledDynamicValueTableColumnTypeLiteral.optional(),
  predicate: ColumnPredicate,
  // these fields below are relevant for filtering in explore cells
  // optional for backwards compatibility
  queryPath: z.array(SemanticDatasetName).optional(),
  // dimensions, measures, and columns
  fieldType: ExploreFieldTypeLiteral.optional(),
  // set if filtering by an aggregated dimension in explore
  aggregation: HqlAggregationFunctionLiteral.optional(),
  // true if filters are active but should not show up in UI (e.g. filters copied over to explore)
  hidden: z.boolean().optional(),
  // set if lump filter should order by a specific field
  lumpOrderBy: ExploreFieldId.optional(),
});
export type ColumnFilter = z.infer<typeof ColumnFilter>;

export const TableFiltersDefinition = z.array(ColumnFilter);
export type TableFiltersDefinition = z.infer<typeof TableFiltersDefinition>;

// graphql scalars
export const FilterPatternMap = z.record(z.string(), z.string());
export type FilterPatternMap = z.infer<typeof FilterPatternMap>;

export const ColumnProfileSemanticDatasetInput = z.object({
  type: z.literal("semantic"),
  semanticDatasetName: SemanticDatasetName,
  semanticProjectId: SemanticProjectId,
  semanticProjectVersionString: SemanticProjectVersionString.nullish(),
});
export type ColumnProfileSemanticDatasetInput = z.infer<
  typeof ColumnProfileSemanticDatasetInput
>;

export const ColumnProfileWarehouseTableInput = z.object({
  type: z.literal("table"),
  dataSourceTableId: DataSourceTableId,
});
export type ColumnProfileWarehouseTableInput = z.infer<
  typeof ColumnProfileWarehouseTableInput
>;

export const ColumnProfileDataframeInput = z.object({
  type: z.literal("dataframe"),
  cellId: CellId,
  appSessionId: AppSessionId,
  dataframeName: z.string(),
});
export type ColumnProfileDataframeInput = z.infer<
  typeof ColumnProfileDataframeInput
>;

export const ColumnProfileInput = z.discriminatedUnion("type", [
  ColumnProfileSemanticDatasetInput,
  ColumnProfileWarehouseTableInput,
  ColumnProfileDataframeInput,
]);
export type ColumnProfileInput = z.infer<typeof ColumnProfileInput>;
